import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";


import {
  getAgents,
  getAgentsOptions,
  getAgent,
  createAgent,
  updateAgent,
  deleteAgent,
  getAgentProperties,
  getDomains,
  getDomain,
  createDomain,
  deleteDomain,
  updateDomain,
  toggleAgentStatus,
  getWidgetSettings,
  manageWidgetSettings,
  deleteWidgetSettings,
  getAgentPropertiesOptions,
  deleteAgentBrochureAndExtractData,
  deleteAgentFloorPlanAndExtractData,
} from "../../api/agents/agent.api.js";

// ---------------- GET ALL AGENTS ----------------
export const useAgentsQuery = () =>
  useQuery({
    queryKey: ["agents"],
    queryFn: getAgents,
  });


// ---------------- GET ALL AGENTS ----------------
export const useAgentsOptionsQuery = (options = {}) =>
  useQuery({
    queryKey: ["agents-options"],
    queryFn: getAgentsOptions,
    ...options,
  });


// ---------------- GET SINGLE AGENT ----------------
export const useAgentQuery = (id) => {
  return useQuery({
    queryKey: ["agents", id],
    queryFn: () => getAgent(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};
// ---------------- CREATE AGENT ----------------
export const useCreateAgentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAgent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", "agents-options"],
        exact: false,
      });
    },
  });
};

// ---------------- UPDATE AGENT ----------------
export const useUpdateAgentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateAgent(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["agents"],
        exact: false,
      });
    },
  });
};

// ---------------- DELETE AGENT ----------------
export const useDeleteAgentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAgent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", "agents-options"],
        exact: false,
      });
    },
  });
};

// ---------------- TOGGLE STATUS ----------------
export const useToggleAgentStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId }) => toggleAgentStatus(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents"],
        exact: false,
      });
    },
  });
};

// ---------------- AGENT PROPERTIES ----------------
export const useAgentPropertiesQuery = (agentId) =>
  useQuery({
    queryKey: ["agents", agentId, "properties"],
    queryFn: () => getAgentProperties(agentId),
    enabled: !!agentId,
  });


// ---------------- AGENT PROPERTIES ----------------
export const useAgentPropertiesOptionsQuery = (agentId) =>
  useQuery({
    queryKey: ["agents", agentId, "properties"],
    queryFn: () =>  getAgentPropertiesOptions(agentId),
    enabled: !!agentId,
  });

// ---------------- DOMAINS ----------------
export const useDomainsQuery = (agentId) =>
  useQuery({
    queryKey: ["agents", agentId, "domains"],
    queryFn: () => getDomains(agentId),
    enabled: !!agentId,
  });

export const useDomainQuery = (agentId, domain) =>
  useQuery({
    queryKey: ["agents", agentId, "domains", domain],
    queryFn: () => getDomain(agentId, domain),
    enabled: !!agentId && !!domain,
  });

export const useCreateDomainMutation = (agentId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createDomain(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "domains"],
      });
    },
  });
};

export const useUpdateDomainMutation = (agentId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain_id, data }) => updateDomain(agentId, domain_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "domains"],
      });
    },
  });
};

export const useDeleteDomainMutation = (agentId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domain) => deleteDomain(agentId, domain),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "domains"],
      });
    },
  });
};


// ---------------- DELETE AGENT BROCHURE AND EXTRACT DATA ----------------
export const useDeleteAgentBrochureAndExtractDataMutation = (agentId, propertyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteAgentBrochureAndExtractData(agentId, propertyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["properties"],
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", agentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "properties"],
      });
    },
  });
};

// ---------------- DELETE AGENT FLOOR PLAN AND EXTRACT DATA ----------------
export const useDeleteAgentFloorPlanAndExtractDataMutation = (agentId, propertyId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteAgentFloorPlanAndExtractData(agentId, propertyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["properties"],
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", agentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "properties"],
      });
    },
  });
};

// ---------------- WIDGET SETTINGS ----------------
export const useGetWidgetSettingsQuery = (agentId) =>
  useQuery({
    queryKey: ["agents", agentId, "widget-settings"],
    queryFn: () => getWidgetSettings(agentId),
    enabled: !!agentId,
  });

export const useManageWidgetSettingsMutation = (agentId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({agentId, data}) => manageWidgetSettings(agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "widget-settings"],
      });
    },
  });
};

export const useDeleteWidgetSettingsMutation = (agentId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({agentId}) => deleteWidgetSettings(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agents", agentId, "widget-settings"],
      });
    },
  });
};
